package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.TeddDatabase
import com.tedd.db.Timer_state
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.nowMillis

/** L'unique ligne du chronomètre partagé. */
private const val TIMER_ID = "current"

/**
 * Chronomètre partagé entre appareils. On n'écrit jamais un temps restant : on écrit
 * l'instant de départ, la durée prévue et le temps passé en pause. Chaque appareil en
 * déduit la même valeur, quelle que soit l'heure à laquelle il ouvre l'application.
 */
class TimerStateRepository(private val db: TeddDatabase) {

    fun observe(): Flow<Timer_state?> =
        db.timerStateQueries.selectCurrent().asFlow().mapToOneOrNull(Dispatchers.Default)

    suspend fun current(): Timer_state? = withContext(Dispatchers.Default) {
        db.timerStateQueries.selectCurrent().executeAsOneOrNull()
    }

    /** Démarre une course : l'instant de départ fait foi pour tous les appareils. */
    suspend fun start(plannedMinutes: Int, itemId: String?, skillId: String?, device: String) =
        withContext(Dispatchers.Default) {
            write(
                running = true,
                startedAt = nowMillis(),
                plannedMinutes = plannedMinutes,
                itemId = itemId,
                skillId = skillId,
                pausedAt = null,
                pausedAccumMs = 0L,
                device = device,
            )
        }

    /** Met en pause : on note l'instant, le temps cumulé s'ajoutera à la reprise. */
    suspend fun pause() = withContext(Dispatchers.Default) {
        val s = current() ?: return@withContext
        if (s.running == 0L || s.paused_at != null) return@withContext
        write(true, s.started_at, s.planned_minutes.toInt(), s.item_id, s.skill_id, nowMillis(), s.paused_accum_ms, s.device)
    }

    suspend fun resume() = withContext(Dispatchers.Default) {
        val s = current() ?: return@withContext
        val pausedAt = s.paused_at ?: return@withContext
        val accum = s.paused_accum_ms + (nowMillis() - pausedAt).coerceAtLeast(0L)
        write(true, s.started_at, s.planned_minutes.toInt(), s.item_id, s.skill_id, null, accum, s.device)
    }

    /** Arrête la course partout. */
    suspend fun stop() = withContext(Dispatchers.Default) {
        val s = current()
        write(false, null, s?.planned_minutes?.toInt() ?: 25, null, null, null, 0L, s?.device)
    }

    /** Change la durée prévue sans toucher au reste. */
    suspend fun setPlannedMinutes(minutes: Int) = withContext(Dispatchers.Default) {
        val s = current()
        write(s?.running == 1L, s?.started_at, minutes, s?.item_id, s?.skill_id, s?.paused_at, s?.paused_accum_ms ?: 0L, s?.device)
    }

    private fun write(
        running: Boolean,
        startedAt: Long?,
        plannedMinutes: Int,
        itemId: String?,
        skillId: String?,
        pausedAt: Long?,
        pausedAccumMs: Long,
        device: String?,
    ) {
        val now = nowMillis()
        val existing = db.timerStateQueries.selectCurrent().executeAsOneOrNull()
        if (existing == null) {
            db.timerStateQueries.insert(
                TIMER_ID, if (running) 1L else 0L, startedAt, plannedMinutes.toLong(), itemId, skillId,
                pausedAt, pausedAccumMs, device, now, now,
            )
        } else {
            db.timerStateQueries.update(
                if (running) 1L else 0L, startedAt, plannedMinutes.toLong(), itemId, skillId,
                pausedAt, pausedAccumMs, device, now, TIMER_ID,
            )
        }
    }
}
